import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, catchError, finalize, of, take } from 'rxjs';
import { Order } from '../../../models/domain/Order';
import { AcceptOrderDto } from '../../../models/domain/AcceptOrderDto';
import { UpdateOrderStatus } from '../../../models/domain/UpdateOrderStatus';
import {
    AllowedTransitions,
    OrderStatus,
    OrderStatusBadgeClass,
    OrderStatusLabels,
} from '../../../models/enums/OrderStatus';
import { LoadingService } from '../../../services/loading.service';
import { OrderService } from '../../../services/order.service';
import { AuthenticationService } from '../../../services/authentication.service';

@Component({
    selector: 'app-order-detail',
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './order-detail.component.html',
    styleUrl: './order-detail.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderDetailComponent implements OnChanges, OnDestroy {

    @Input() orderId: number = 0;
    @Output() classApplied = new EventEmitter<boolean>();
    @Output() statusUpdated = new EventEmitter<void>();

    order: Order | null = null;
    isLoading = false;
    isSaving = false;

    statusForm!: FormGroup;
    allowedStatuses: { value: OrderStatus; label: string }[] = [];

    /** Global rate applied to all items at once */
    globalRateControl = new FormControl<number | null>(null);

    /** Per-item rate controls keyed by orderItemId */
    itemRateMap = new Map<number, FormControl<number | null>>();

    /** Payment terms from JWT token (read-only display) */
    paymentTerms: string[] = [];

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly orderService: OrderService,
        private readonly fb: FormBuilder,
        private readonly toaster: ToastrService,
        private readonly loadingService: LoadingService,
        private readonly cdr: ChangeDetectorRef,
        private readonly authService: AuthenticationService,
    ) {
        this.statusForm = this.fb.group({
            newStatus: new FormControl<string | null>(null, Validators.required),
            remark: new FormControl<string>('', Validators.maxLength(500)),
        });

        this.paymentTerms = this.authService.getCurrentUser()?.paymentTerms ?? [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['orderId'] && this.orderId > 0) {
            this.statusForm.reset();
            this.globalRateControl.reset();
            this.itemRateMap.clear();
            this.loadOrder();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get isOrderPlaced(): boolean {
        return this.order?.orderStatus === OrderStatus.OrderPlaced;
    }

    private loadOrder(): void {
        this.isLoading = true;
        this.cdr.markForCheck();

        this.orderService
            .getOrderById(this.orderId)
            .pipe(
                take(1),
                catchError((error) => {
                    const msg = error?.error?.isCustom
                        ? error.error.message
                        : 'Failed to load order.';
                    this.toaster.error(msg);
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                    this.cdr.markForCheck();
                }),
            )
            .subscribe((result) => {
                if (result?.body) {
                    this.order = result.body;
                    this.buildAllowedStatuses();
                    this.buildItemRateControls();
                    this.cdr.markForCheck();
                }
            });
    }

    private buildAllowedStatuses(): void {
        if (!this.order) return;
        const current = this.order.orderStatus as OrderStatus;
        const transitions = AllowedTransitions[current] ?? [];
        this.allowedStatuses = transitions.map((s) => ({
            value: s,
            label: OrderStatusLabels[s],
        }));
        const ctrl = this.statusForm.get('newStatus')!;
        if (this.allowedStatuses.length > 0) {
            ctrl.setValidators(Validators.required);
        } else {
            ctrl.clearValidators();
        }
        ctrl.updateValueAndValidity();
    }

    private buildItemRateControls(): void {
        this.itemRateMap.clear();
        if (!this.order) return;
        for (const item of this.order.orderItems) {
            this.itemRateMap.set(
                item.orderItemId,
                new FormControl<number | null>(item.rate ?? null),
            );
        }
    }

    public getRateControl(orderItemId: number): FormControl<number | null> {
        return this.itemRateMap.get(orderItemId) ?? new FormControl<number | null>(null);
    }

    /** Called on every keystroke in the global rate field — auto-fills all items */
    public onGlobalRateInput(): void {
        const rate = this.globalRateControl.value;
        if (rate == null || rate <= 0) return;
        this.itemRateMap.forEach((ctrl) => ctrl.setValue(rate));
        this.cdr.markForCheck();
    }

    public getStatusLabel(status: string): string {
        return OrderStatusLabels[status as OrderStatus] ?? status;
    }

    public getStatusClass(status: string): string {
        return OrderStatusBadgeClass[status as OrderStatus] ?? 'badge bg-secondary';
    }

    public submitStatusUpdate(): void {
        if (this.statusForm.invalid) return;

        const selectedStatus = this.statusForm.value.newStatus as OrderStatus;
        const remark: string = this.statusForm.value.remark ?? '';

        this.isSaving = true;
        this.loadingService.showLoading();

        // When accepting, use the accept endpoint so per-item rates are saved.
        if (selectedStatus === OrderStatus.Accepted) {
            const itemRates: { orderItemId: number; rate: number }[] = [];
            this.itemRateMap.forEach((ctrl, id) => {
                const rate = ctrl.value;
                if (rate != null && rate > 0) itemRates.push({ orderItemId: id, rate });
            });

            const dto: AcceptOrderDto = {
                orderId: this.orderId,
                globalRate: this.globalRateControl.value ?? undefined,
                itemRates,
                remark,
            };

            this.orderService.acceptOrder(dto)
                .pipe(
                    take(1),
                    catchError((error) => {
                        const msg = error?.error?.isCustom ? error.error.message : 'Failed to accept order.';
                        this.toaster.error(msg);
                        return of(null);
                    }),
                    finalize(() => {
                        this.isSaving = false;
                        this.loadingService.hideLoading();
                        this.cdr.markForCheck();
                    }),
                )
                .subscribe((result) => {
                    if (result?.body) {
                        this.toaster.success('Order accepted successfully.');
                        this.statusUpdated.emit();
                        this.classApplied.emit(false);
                    }
                });
        } else {
            const dto: UpdateOrderStatus = {
                orderId: this.orderId,
                newStatus: selectedStatus,
                remark,
            };

            this.orderService.updateStatus(dto)
                .pipe(
                    take(1),
                    catchError((error) => {
                        const msg = error?.error?.isCustom ? error.error.message : 'Failed to update status.';
                        this.toaster.error(msg);
                        return of(null);
                    }),
                    finalize(() => {
                        this.isSaving = false;
                        this.loadingService.hideLoading();
                        this.cdr.markForCheck();
                    }),
                )
                .subscribe((result) => {
                    if (result?.body) {
                        this.toaster.success('Order status updated successfully.');
                        this.statusUpdated.emit();
                        this.classApplied.emit(false);
                    }
                });
        }
    }

    public close(): void {
        this.classApplied.emit(false);
    }
}
